// THE COMPILER CORE (Process-Compiled Agent, Wave 2 — W2-2).
//
// One emitter, two inputs. It used to be `buildVerticalFlow(greeting,
// handoffReason, cats)`: a typed-action → subgraph emitter whose only defect was
// that its input was a per-vertical literal list hardcoded in
// `buildStarterOrchestrator`. It now consumes a `CompilerInput`, which carries
// BOTH the tenant's connected processes (the new half) and the declared
// categories (the vertical presets' half, unchanged).
//
// BYTE-IDENTITY IS THE REGRESSION ALARM. `buildVerticalFlow` survives as a thin
// wrapper in `bot-config/orchestrator-scaffold.ts`: it builds the CompilerInput
// equivalent of its three arguments and calls `compileChannelAgent`. Every
// existing test of the seeded starter flows passes UNTOUCHED, which is what says
// the de-hardcoding changed no output.
//
// DETERMINISM IS A CONTRACT, not a happy accident. The diff feature of a later
// wave compares two compilations of the same Agente, so the same input must
// produce byte-identical output forever: no `Date.now`, no randomness, no
// counters that depend on iteration order of an object. Every node id is DERIVED
// — from a process key, a stage key or a category key, all of them tenant slugs
// — exactly as the scaffold's ids always were (`llamar_${key}`,
// `guardar_${key}`, `pedir_${key}_${row}`). Ordering is taken from `position`,
// not from array order, so a caller that read rows unsorted compiles the same
// Agente.
//
// PURE + web-safe: no React, no DB, no node builtins, no side effects.

import {
  assertNoDanglingEdges,
  type BookingSegmentIds,
  buildBookingSegment,
  DEFAULT_BOOKING_COPY,
} from './booking-segment.js'
import { buildCaptureFlow, captureCategoryLabelFor } from './capture-flow.js'
import {
  type AgendaMissingDatum,
  AgendaStageNeedsDataError,
  type CaptureFlowPlan,
  CaptureFlowHasNoQuestionsError,
  captureFlowOptionsOf,
  captureWindowOf,
  type FichaField,
  planCaptureFlow,
} from './capture-plan.js'
import type { AgentTerminal } from './diff.js'
import { type StarterEdge, type StarterFlow, type StarterNode, whenForIntent } from './flow.js'
import type { CompilerCategory, CompilerCopy, CompilerInput, CompilerStage } from './types.js'

/** One callable capture the compilation produced: the task flow itself plus the
 *  plan it came from, so the caller can publish it and say what it will ask
 *  BEFORE anything is written. */
export interface CompiledCaptureFlow {
  /** The process this capture belongs to — also the menu option key. */
  processKey: string
  /** Las etapas de `captura` que ESTE flujo compila, en el orden en que la
   *  conversación las recorre: un TRAMO por columna (F3-1c). La tarjeta nace en
   *  la primera y va avanzando conforme el agente aprende lo que cada una
   *  obtiene. Con la caída a la ficha completa trae una sola, que es la etapa de
   *  siempre (`plan.stage`). */
  stageKeys: string[]
  /** The `call` node in the orchestrator that hands the turn to this flow. */
  callNodeId: string
  plan: CaptureFlowPlan
  flow: StarterFlow
}

/** A stage the PREVIEW draws but the compiler never emits (D6). */
export interface CompiledVirtualStage {
  kind: 'consent'
  name: string
}

/** A `call` node whose callee has not been published yet: the orchestrator names
 *  the node, the caller publishes `CompiledCaptureFlow.flow`, and the linking
 *  wave writes the id back. Reported rather than silently emitted as a valid-
 *  looking id, because a `call` pointing at nothing escalates with `dead_end`. */
export interface PendingCaptureLink {
  processKey: string
  callNodeId: string
}

/** UN PROCESO QUE QUIERE AGENDAR Y NO PUEDE (D22-6).
 *
 *  Su etapa de `agenda` no tiene con qué buscar horario — falta el dato de
 *  interés, o falta la fecha —, así que se compiló SIN reserva: las preguntas
 *  del proceso siguen funcionando y la vista previa no truena. Se reporta aquí,
 *  con la causa, para que la puerta de publicación lo pueda rechazar diciendo
 *  qué falta en vez de publicar un agente que promete una cita que no sabe
 *  buscar. Un proceso NUNCA agenda a medias. */
export interface AgendaCompileRefusal {
  processKey: string
  stageKey: string
  stageName: string
  missing: readonly AgendaMissingDatum[]
  /** La causa, en la frase que se le enseña al cliente. */
  message: string
}

export interface CompiledChannelAgent {
  /** El flujo principal: el menú hoisted y sus ramas. */
  orchestrator: StarterFlow
  /** Un flujo llamable por proceso con etapa de `captura`, en el orden del menú. */
  captureFlows: CompiledCaptureFlow[]
  /** Lo que la vista previa dibuja y el compilador NO emite (D6). */
  virtualStages: CompiledVirtualStage[]
  /** Los `call` cuyo destino todavía no existe. Vacío cuando cada proceso trajo
   *  su `captureSkillId`. */
  pendingCaptureLinks: PendingCaptureLink[]
  /** Los procesos que se compilaron SIN su reserva porque la etapa que agenda no
   *  tiene con qué (D22-6). Vacío en todo lo demás. */
  agendaRefusals: AgendaCompileRefusal[]
}

/** Cómo se llama la etapa virtual del consentimiento en la vista previa. Una
 *  sola fuente: el compilador la nombra, la pantalla la dibuja. */
export const CONSENT_VIRTUAL_STAGE_NAME = 'Consentimiento'

/** A dónde va el "sí, agéndalo" de una cotización — o el "reagendar" de un
 *  cambio de cita — cuando ESTE agente no tiene rama de reserva a la que saltar.
 *
 *  Con una persona, no a `fin_ok`. Terminar ahí dejaba la conversación MUDA
 *  justo después de que el cliente dijo que sí: la pregunta se hizo, la
 *  respuesta se entendió, y el agente se despedía sin decir palabra. Un giro con
 *  catálogo y sin agenda es exactamente ese caso. `pasar` es el traspaso
 *  compartido del flujo, el mismo al que llega una categoría de traspaso, y
 *  vuelve al Descubrir cuando el asesor termina. */
export const BOOK_ENTRY_FALLBACK = 'pasar'

/** Los textos de hoy, palabra por palabra. Eran literales DENTRO del emisor —
 *  preguntas de una clínica que una agencia heredaba sin pedirlas — y ahora son
 *  la omisión de un campo de entrada. Cambiar cualquiera de estas cadenas
 *  cambia el flujo de arranque de todos los giros, así que se cambian aquí y el
 *  test de equivalencia lo nota. */
const DEFAULT_COPY: Required<CompilerCopy> = {
  cierre: '¡Con gusto! Que estés muy bien. Aquí estoy si necesitas algo más.',
  motivoPrompt: '¿Cuál es el motivo de tu consulta?',
  primeraVisitaPrompt: '¿Es tu primera visita con nosotros? Responde sí o no.',
  coberturaPrompt: '¿Usarás seguro médico? Dime tu aseguradora, o escribe "particular".',
  // Los cuatro del TRAMO DE RESERVA salen de su propia casa (D22-6): la etapa
  // `agenda` de un proceso emite ese mismo tramo, y dos copias de estas frases
  // serían el mismo paso diciendo cosas distintas según quién lo emitió.
  ofrecerExacto: DEFAULT_BOOKING_COPY.ofrecerExacto,
  avisoAlternativas: DEFAULT_BOOKING_COPY.avisoAlternativas,
  listarOpciones: DEFAULT_BOOKING_COPY.listarOpciones,
  citaConfirmada:
    '¡Listo! Tu cita quedó agendada para ${elegido.starts_at_label} con ${elegido.doctor_name}. Tu número de confirmación es ${reserva.booking_id}.',
  citaPendiente:
    'Listo, te aparté ${elegido.starts_at_label} con ${elegido.doctor_name}. Queda pendiente de que el consultorio la confirme; te aviso por aquí en cuanto esté lista. Tu folio es ${reserva.booking_id}.',
  horarioOcupado: DEFAULT_BOOKING_COPY.horarioOcupado,
  precio: 'El costo de ${cotizacion.name} es $${cotizacion.price_mxn} pesos.',
  ofrecerAgendar: '¿Te gustaría agendar una cita? Responde sí o no.',
  citaEncontrada:
    'Encontré tu cita de ${cita_actual.specialty} el ${cita_actual.starts_at_label} con ' +
    '${cita_actual.doctor_name}. ¿Quieres cancelarla, reagendarla, o dejarla como está?',
  citaCancelada: 'Listo, tu cita quedó cancelada. Si quieres agendar otra, aquí estoy.',
  reagendarAviso: 'Con gusto. Cancelo la cita anterior y buscamos un nuevo horario.',
  reagendarConPersona:
    'Con gusto. Te paso con una persona del equipo para mover tu cita a otro horario.',
  citaSinCambios: 'Perfecto, tu cita sigue en pie. ¿Te ayudo con algo más?',
}

/** Order by `position`, ties broken by `key`, so the result is a TOTAL order and
 *  two callers that read the same rows compile the same Agente. */
function byPosition<T extends { position: number; key: string }>(a: T, b: T): number {
  return a.position - b.position || (a.key < b.key ? -1 : a.key > b.key ? 1 : 0)
}

/** Las columnas de un proceso en el orden TOTAL del compilador: `position`, y
 *  la clave para desempatar.
 *
 *  Existe por la misma razón que `terminalOfProcess`: la vista previa (F2-6)
 *  dibuja las etapas una por una y tiene que enseñarlas en el orden en el que
 *  el emisor las lee, no en el que la base las devolvió. Una segunda copia del
 *  criterio de orden en `packages/api` haría que la pantalla pudiera enseñar
 *  una secuencia que el agente no corre. */
export function orderedStages(stages: readonly CompilerStage[]): CompilerStage[] {
  return [...stages].sort(byPosition)
}

/** La VENTANA de captura de un proceso: qué columnas compila su captura, y a
 *  dónde termina su rama una vez recorridas.
 *
 *  UNA SOLA REGLA, y por eso este envoltorio existe (F3-1c). Desde que la
 *  captura se reparte en TRAMOS, la terminal ya no se lee después de la primera
 *  columna de captura sino después de la ÚLTIMA que se compila, y esa elección
 *  la hace `selectCaptureTramos` mirando la ficha. El emisor y la vista previa
 *  la llaman los dos: con dos copias, la pantalla podría prometer una terminal
 *  que el Agente no corre.
 *
 *  Sin NINGUNA columna de `captura` no hay tramos y la terminal se lee desde el
 *  principio del proceso, que es lo que el emisor hacía desde siempre. */
export function processCaptureWindow(
  stages: readonly CompilerStage[],
  ficha: readonly FichaField[] = [],
): { compiledStages: CompilerStage[]; terminal: AgentTerminal } {
  // EL CÁLCULO VIVE EN `capture-plan.ts` (F4-6): desde que la terminal es un
  // control visible la lee también el PLAN — la confirmación y el cierre del
  // flujo llamado dependen de ella —, y `compile.ts` no puede ser su casa
  // porque ese archivo importa a éste y no al revés.
  return captureWindowOf(stages, ficha)
}

/** A dónde termina la rama de ESTE proceso, con las columnas que trae.
 *
 *  Existe para que la VISTA PREVIA (W2-4/W2-5) pueda decir "la conversación
 *  termina con una persona" leyendo exactamente la misma regla que el emisor
 *  aplica, en vez de una segunda copia suya en `packages/api`. Una regla de
 *  D1/D2 escrita dos veces es la deriva que este ciclo existe para terminar.
 *
 *  La FICHA es parte de la regla desde los tramos (F3-1c): es lo que decide
 *  cuál es la última columna que la captura compila. Sin ella se lee la
 *  terminal de un proceso cuyas columnas no declaran nada, que es la caída a la
 *  ficha completa: un solo tramo, el primero. */
export function terminalOfProcess(
  stages: readonly CompilerStage[],
  ficha: readonly FichaField[] = [],
): AgentTerminal {
  return processCaptureWindow(stages, ficha).terminal
}

/**
 * Compila el Agente de UNA identidad de canal.
 *
 * D15: la conexión es dueña de UN menú de nivel superior. Cada proceso conectado
 * aporta UNA opción, en el orden de la conexión, y sus etapas dicen qué hace esa
 * rama (D1/D2). Después van las categorías declaradas: los presets de giro y la
 * cola genérica de responder / pasar con una persona / despedirse.
 *
 * D6: el consentimiento NO se compila. Se devuelve en `virtualStages` para que
 * la vista previa lo dibuje como primera etapa; quien lo ejecuta es la puerta
 * del pipeline (paso 3b), no este flujo.
 */
export function compileChannelAgent(input: CompilerInput): CompiledChannelAgent {
  const copy: Required<CompilerCopy> = { ...DEFAULT_COPY, ...input.copy }
  const { greeting, handoffReason } = input
  const processes = [...(input.processes ?? [])].sort(byPosition)
  const cats: readonly CompilerCategory[] = input.categories ?? []
  // Layout grid. The starter flow is auto-generated, so lay it out so a person
  // can actually READ it in the editor: shared nodes in a left column; each
  // Descubrir branch in its own well-separated vertical lane to the right; wide
  // enough row/column spacing that nodes and arrows never overlap.
  const TOP = 380 // y where branch lanes begin (below discover)
  const ROW = 130 // vertical gap between chained nodes
  const COL = 260 // horizontal gap between a lane's columns
  const LANE_GAP = 180 // extra gap between one branch lane and the next
  let laneX = 360

  // EL MENÚ (D15). Se arma antes que los nodos porque el `route` lo lleva
  // entero: primero los procesos conectados, en el orden de la conexión, y
  // después las categorías declaradas. La etiqueta de un proceso es la de la
  // conexión; cuando la conexión no trae ninguna, se DERIVA del nombre del
  // proceso en vez de guardar una copia que deja de seguir los renombres.
  //
  // Y CADA OPCIÓN LLEVA SU SIGNIFICADO (F5-2). La etiqueta es lo que el menú
  // DICE; la pista es lo que el clasificador ENTIENDE, y sólo se escribe cuando
  // el cliente escribió un significado: sin él la pista sería la etiqueta otra
  // vez, y una opción que repite su etiqueta en dos campos no le agrega nada al
  // modelo y hace distinta la emisión de todo proceso que no configuró nada.
  //
  // LAS CATEGORÍAS SE AGREGAN DESDE SU PROPIO RECORRIDO (D22-7). El arreglo nace
  // con los procesos y el `route` se lo lleva por referencia; cada categoría
  // empuja su opción cuando se emite su rama, unas líneas más abajo. Es lo que
  // deja que una categoría que NO se emite tampoco aparezca en el menú, sin
  // tener que decidir dos veces — aquí y allá — cuáles se emiten.
  const menuOptions: { key: string; label: string; hint?: string }[] = processes.map((p) => {
    // `?? undefined` y no `!== null` a secas: un llamador que todavía no mande
    // el campo lo deja en `undefined`, y `{ hint: undefined }` es una opción
    // con una llave de más que ninguna otra opción lleva.
    const hint = p.meaning ?? undefined
    return {
      key: p.key,
      label: p.entryLabel ?? captureCategoryLabelFor(p.name),
      ...(hint === undefined ? {} : { hint }),
    }
  })

  const nodes: StarterNode[] = [
    { id: 'start', type: 'msg', position: { x: 360, y: 40 }, config: { text: greeting } },
    {
      id: 'discover',
      type: 'route',
      position: { x: 360, y: 200 },
      // D15: UN solo menú de nivel superior, y la conexión es su dueña. Los
      // procesos conectados van primero, en el orden de la conexión; las
      // categorías declaradas después.
      config: { var: 'intent', options: menuOptions },
    },
    { id: 'responder', type: 'answer', position: { x: 40, y: 380 }, config: {} },
    {
      id: 'pasar',
      type: 'handoff',
      position: { x: 40, y: 540 },
      config: { reason: handoffReason },
    },
    {
      id: 'cierre',
      type: 'msg',
      position: { x: 40, y: 700 },
      config: { text: copy.cierre },
    },
    { id: 'fin_ok', type: 'fin', position: { x: 40, y: 860 }, config: { kind: 'ok' } },
  ]
  // Category branches FIRST (first-match wins), THEN the default "true" branch —
  // otherwise the default would always win and the categories never fire.
  const edges: StarterFlow['edges'] = [
    { from: 'start', to: 'discover' },
    { from: 'cierre', to: 'fin_ok' }, // say goodbye before ending (never a silent stop)
  ]
  // ── Los procesos conectados (D15) ────────────────────────────────────────
  //
  // Una opción del menú por proceso, y lo que hace su rama sale de los BEHAVIORS
  // de sus columnas (D1/D2), no de un giro escrito a mano:
  //
  //   captura        → un flujo llamable cuyas preguntas siguen la ficha del
  //                    proceso; el menú gana un nodo `call` que le entrega el
  //                    turno.
  //   llenado_manual → NADA. La columna la llena una persona, así que el Agente
  //                    no pregunta esos datos (D2). El solver los cuenta en una
  //                    ola posterior; emitir preguntas aquí sería el Agente
  //                    haciendo el trabajo de quien opera.
  //   handoff / fin  → la terminal de la rama, ya existente y compartida
  //                    (`pasar` / `cierre` → `fin_ok`).
  //   inicio         → NADA más allá de ser el punto de entrada: la opción del
  //                    menú ES la entrada del proceso, así que no hay nodo que
  //                    emitir. Es la emisión mínima correcta.
  //   multiplexor    → NADA todavía.
  //                    TODO(Wave 4 — el multiplexor): los saltos entre procesos.
  //
  // TODO(Wave 6 — D20 overrides): los ajustes por pregunta (prompt/reintentos
  // autorados) entran aquí, no en el emisor.
  const captureFlows: CompiledCaptureFlow[] = []
  const pendingCaptureLinks: PendingCaptureLink[] = []
  const agendaRefusals: AgendaCompileRefusal[] = []
  /** Las aristas de "sí, agéndalo" (la cotización) y de "reagendar" (el cambio
   *  de cita): las dos SALTAN a la entrada de la rama de reserva, que puede no
   *  existir en este agente. Se anotan aquí y se resuelven cuando ya se emitió
   *  todo, porque una categoría que cotiza se puede emitir ANTES que la que
   *  agenda y mirar los nodos a medio emitir daría una respuesta distinta según
   *  el orden del menú. */
  const bookEntryEdges: StarterFlow['edges'] = []
  processes.forEach((p) => {
    const gx = (col: number): number => laneX + col * COL
    const gy = (row: number): number => TOP + row * ROW
    const stages = [...p.stages].sort(byPosition)
    const window = processCaptureWindow(stages, p.ficha)
    /** Un proceso que no pregunta nada: la opción del menú lleva directo a donde
     *  el proceso termina. Es la emisión mínima correcta, y desde F4-10 la
     *  comparten DOS caminos que llegan al mismo hecho — ninguna columna que
     *  compilar, y ninguna pregunta que hacer. */
    const emitNoCapture = (): void => {
      edges.push({
        from: 'discover',
        to: window.terminal,
        when: whenForIntent('intent', p.key),
      })
    }
    if (window.compiledStages.length === 0) {
      emitNoCapture()
      return
    }
    // La ficha del proceso baja a preguntas con el MISMO cálculo que usa la
    // generación desde la pantalla (C1), no con una segunda copia de la regla.
    // Las columnas van TODAS: el plan reparte las preguntas entre ellas (F3-1b)
    // con la misma regla con la que arriba se leyó la terminal.
    //
    // Y UN PROCESO QUE NO PREGUNTARÍA NADA SE COMPILA, NO TRUENA (F4-10). Con
    // el último dato de su ficha desactivado — el `nombre` incluido, que desde
    // F5-1 es uno de ellos — el plan no existe: hay columna de captura pero no
    // hay captura. Es el MISMO
    // hecho que una columna que no compila — el agente no pregunta nada en este
    // proceso — así que se emite igual, por el mismo camino. Dejar salir el
    // error tumbaba con un 500 la vista previa del agente entero, y con ella
    // toda la pantalla, por un proceso que quedó mudo.
    let plan: CaptureFlowPlan
    const planInput = {
      pipelineName: p.name,
      pipelineKey: p.key,
      // La MISMA etiqueta resuelta con la que el menú de arriba se armó (F4-5).
      // El plan la vuelve a coalescer con la derivada, así que las dos ramas del
      // mismo proceso — la opción y el flujo que llama — dicen lo mismo.
      entryLabel: p.entryLabel,
      // El significado viaja con el plan (F5-2) para que la PISTA de la opción
      // y el flujo que esa opción llama salgan de la misma lectura.
      meaning: p.meaning,
      // Si este proceso pregunta el nombre, y con qué frase, ya no viaja
      // aparte (F5-1): viene DENTRO de `ficha`, como el renglón `nombre` que
      // el proceso lleva o no lleva.
      stages,
      ficha: p.ficha,
    }
    try {
      plan = planCaptureFlow(planInput)
    } catch (err) {
      if (err instanceof AgendaStageNeedsDataError) {
        // D22-6 — UN PROCESO NUNCA AGENDA A MEDIAS. Su etapa de `agenda` no
        // tiene con qué buscar horario, así que se compila SIN reserva: la
        // columna vuelve a ser una captura como cualquier otra, el agente sigue
        // preguntando lo que su proceso declara y la vista previa no truena por
        // una columna a medio configurar. La causa se anota para que la puerta
        // de publicación pueda rechazarlo diciendo qué falta.
        agendaRefusals.push({
          processKey: p.key,
          stageKey: err.stageKey,
          stageName: err.stageName,
          missing: err.missing,
          message: err.message,
        })
        try {
          plan = planCaptureFlow({
            ...planInput,
            stages: stages.map((s) =>
              s.behavior === 'agenda' ? { ...s, behavior: 'captura' as const } : s,
            ),
          })
        } catch (again) {
          if (!(again instanceof CaptureFlowHasNoQuestionsError)) throw again
          emitNoCapture()
          return
        }
      } else if (err instanceof CaptureFlowHasNoQuestionsError) {
        emitNoCapture()
        return
      } else {
        throw err
      }
    }
    captureFlows.push({
      processKey: p.key,
      stageKeys: plan.stages.map((s) => s.stageKey),
      callNodeId: plan.callNodeId,
      plan,
      // Cómo se emite el plan lo dice `captureFlowOptionsOf`, que es la MISMA
      // regla que usa la generación desde la pantalla de Procesos: dos emisores
      // del mismo plan no pueden escribir dos flujos distintos.
      //
      // Ahí viaja también la TERMINAL (F4-6), que es la misma `window.terminal`
      // de arriba: el cierre del flujo llamado y la arista de salida del `call`
      // se leen juntas. Con `pasar` el llamado traspasa y esa arista no se toma;
      // con `cierre` o `fin_ok` el llamado devuelve el turno y la arista es la
      // que despide o sigue atendiendo.
      flow: buildCaptureFlow(captureFlowOptionsOf(plan)),
    })
    if (p.captureSkillId === null)
      pendingCaptureLinks.push({ processKey: p.key, callNodeId: plan.callNodeId })
    // Un solo nodo `call`: el flujo llamado toma el turno y hace sus preguntas.
    // Su arista de salida es obligatoria — sin una, el runtime no tiene punto de
    // retorno y escala con `dead_end`.
    nodes.push({
      id: plan.callNodeId,
      type: 'call',
      position: { x: gx(0), y: gy(0) },
      config: { skill_id: p.captureSkillId ?? '' },
    })
    edges.push({ from: 'discover', to: plan.callNodeId, when: whenForIntent('intent', p.key) })
    edges.push({ from: plan.callNodeId, to: window.terminal })
    laneX += COL + LANE_GAP
  })

  /** ¿ALGÚN PROCESO CONECTADO AGENDA DE VERDAD? (D22-7).
   *
   *  Se lee de lo que SE EMITIÓ, no de las columnas crudas: un tramo de `agenda`
   *  dentro de un flujo de captura ya compilado. Por eso el proceso cuya etapa
   *  de agenda se RECHAZÓ por falta de datos (D22-6) no cuenta — se recompiló
   *  como captura y su tramo dejó de ser `agenda` —, y un proceso que agenda
   *  fuera de su ventana de captura tampoco: no reserva nadie a quien la
   *  conversación no llega. */
  const anyProcessBooks = captureFlows.some((c) => c.plan.stages.some((s) => s.kind === 'agenda'))

  cats.forEach((c) => {
    // Grid helpers for THIS branch's lane (col 0 = the lane's left column).
    const gx = (col: number): number => laneX + col * COL
    const gy = (row: number): number => TOP + row * ROW
    // LA CITA DE FÁBRICA SE RETIRA CUANDO UN PROCESO AGENDA (D22-7). El proceso
    // conectado es dueño de la reserva: dos ramas que agendan en el mismo menú
    // son dos citas distintas para el mismo paciente, una con la ficha del
    // proceso y otra sin ella. Se filtra por la ACCIÓN y nunca por la clave
    // ('cita' es plomería por giro; agendar es el hecho), la misma lectura que
    // hace `categorySourceOf`.
    //
    // NO SE FILTRA `input.categories` RÍO ARRIBA: el lado VIVO lee su origen de
    // esa misma lista, así que quitarla de ahí haría que la opción viva de cita
    // se leyera como `otra` y bloqueara con `unaccounted_options` justo el
    // Publicar que viene a retirarla. Se deja de EMITIR, que es otra cosa.
    if (anyProcessBooks && c.action.kind === 'schedule') return
    menuOptions.push({ key: c.key, label: c.label })
    if (c.action.kind === 'capture') {
      const gid = `guardar_${c.key}`
      nodes.push({
        id: gid,
        type: 'act',
        position: { x: gx(0), y: gy(0) },
        config: {
          tool: 'capturar_lead',
          args: { nombre: '${contact.profile_name}', interes: c.action.interes },
          out: 'lead',
        },
      })
      edges.push({ from: 'discover', to: gid, when: whenForIntent('intent', c.key) })
      edges.push({ from: gid, to: 'pasar' })
      laneX += COL + LANE_GAP
    } else if (c.action.kind === 'book') {
      // ask₀ → ask₁ → … → Guardar en CRM (remembering the answers) → confirm
      // message → Pasar con una persona (who completes the booking).
      const action = c.action
      let prev = 'discover'
      let row = 0
      action.asks.forEach((a) => {
        const aid = `pedir_${c.key}_${row}`
        nodes.push({
          id: aid,
          type: 'ask',
          position: { x: gx(0), y: gy(row) },
          config: { prompt: a.prompt, var: a.var, extract: { kind: 'string' }, max_retries: 2 },
        })
        edges.push(
          prev === 'discover'
            ? { from: 'discover', to: aid, when: whenForIntent('intent', c.key) }
            : { from: prev, to: aid },
        )
        prev = aid
        row += 1
      })
      const gid = `guardar_${c.key}`
      // El interés es una COLUMNA de la tarjeta, no un dato de la ficha: la
      // respuesta marcada viaja en el argumento `interes` y sale de `campos`.
      // Lo demás sí es ficha, y su clave tiene que existir en el catálogo del
      // giro o el dato queda huérfano y de sólo lectura en la ficha.
      const interestAsk = action.asks.find((a) => a.interest)
      const campos: Record<string, string> = {}
      action.asks.forEach((a) => {
        if (a.interest) return
        campos[a.var] = `\${${a.var}}`
      })
      nodes.push({
        id: gid,
        type: 'act',
        position: { x: gx(0), y: gy(row) },
        config: {
          tool: 'capturar_lead',
          args: {
            nombre: '${contact.profile_name}',
            interes: interestAsk ? `\${${interestAsk.var}}` : action.interes,
            campos,
          },
          out: 'lead',
        },
      })
      edges.push({ from: prev, to: gid })
      const cid = `confirmar_${c.key}`
      nodes.push({
        id: cid,
        type: 'msg',
        position: { x: gx(0), y: gy(row + 1) },
        config: { text: action.confirm },
      })
      // F6-5 — la confirmación PROMETE un asesor ("un asesor te contactará por
      // aquí"), así que el traspaso tiene que pasar en este mismo turno. Con el
      // nodo `pasar` no pasaba: el runtime pausa después de un `msg` cuyo
      // siguiente nodo no es `ask`/`fin`/`route`, y la promesa salía sola. El
      // `fin.handoff` sí lo alcanza. La razón es la que este tramo significa: la
      // tarjeta quedó escrita y una persona la sigue — el mismo nombre que usa
      // el flujo de captura para el mismo hecho.
      const fid = `fin_confirmar_${c.key}`
      nodes.push({
        id: fid,
        type: 'fin',
        position: { x: gx(0), y: gy(row + 2) },
        config: { kind: 'handoff', reason: 'lead_capturado' },
      })
      edges.push({ from: gid, to: cid })
      edges.push({ from: cid, to: fid })
      laneX += COL + LANE_GAP
    } else if (c.action.kind === 'schedule') {
      // Real conversational booking against the doctor's live horarios:
      //   ask especialidad + preferred day/time
      //   → lookup_availability (parses the date, ranks by proximity, says
      //      whether the exact time is free)
      //   → if the requested time is free: offer it (sí/no)
      //   → else / on "no": list the closest options, patient picks a number
      //   → select_slot maps the pick → book_appointment (real cita)
      //   → confirm (branch on status: held-pending vs auto-confirmed)
      // No availability at all → hand to a coordinator.
      const action = c.action
      const k = c.key
      // LOS IDS DE SIEMPRE, palabra por palabra. Los de la entrada, los de la
      // toma de datos de la visita y los de la confirmación son de ESTA rama;
      // los del tramo de reserva viajan enteros al emisor, que no deriva ni
      // prefija ni uno solo (D22-4).
      const n = {
        esp: `pedir_${k}_especialidad`,
        fecha: `pedir_${k}_fecha`,
        motivo: `motivo_${k}`,
        primera: `primera_${k}`,
        seguro: `seguro_${k}`,
        confirmada: `confirmada_${k}`,
        pendiente: `pendiente_${k}`,
      }
      const segmentIds: BookingSegmentIds = {
        buscar: `buscar_${k}`,
        hay: `hay_${k}`,
        exacto: `exacto_${k}`,
        ofrecer: `ofrecer_${k}`,
        decidirDirecto: `decidir_directo_${k}`,
        elegirDirecto: `elegir_directo_${k}`,
        aviso: `aviso_${k}`,
        listar: `listar_${k}`,
        elegirLista: `elegir_lista_${k}`,
        reservar: `reservar_${k}`,
        confirmar: `confirmar_${k}`,
        ocupado: `ocupado_${k}`,
        leadNoofrece: `lead_noofrece_${k}`,
        noofrece: `noofrece_${k}`,
        finNoofrece: `fin_noofrece_${k}`,
        leadSin: `lead_sin_${k}`,
        sin: `sin_${k}`,
        finSin: `fin_sin_${k}`,
      }
      // F6-5 — LO QUE SE PROMETE, SE CUMPLE, Y EL PROSPECTO NO SE EVAPORA. Las
      // dos salidas por las que la reserva se rinde guardan al prospecto ANTES
      // de prometer un coordinador; el porqué vive con quien las emite
      // (`booking-segment.ts`). Lo que se escribe es lo único que esta rama sabe
      // de cierto: la especialidad pedida (la COLUMNA de interés) y la fecha que
      // pidió (`fecha_preferida`, que es dato de la ficha del giro salud). Sin
      // `embudo` ni `etapa`: el proceso por omisión y su primera etapa abierta,
      // que es donde el dueño mira.
      //
      // W4'-6b — LA FICHA GUARDA VALORES, NO FRASES. `fecha_preferida` toma el
      // valor YA INTERPRETADO que `lookup_availability` devuelve
      // (`requested_start`: instante ISO con día y hora, fecha `YYYY-MM-DD`
      // con día solo), no la frase cruda ("mañana a las 3 de la tarde") que
      // nadie puede ordenar ni comparar. Las plantillas de argumentos no saben
      // decir "esto, y si no, aquello" (render.ts no tiene `??`), así que
      // cuando el parser no entendió nada el valor llega null y `capturar_lead`
      // lo descarta antes de escribir, en vez de pisar la ficha con un vacío.
      const citaLeadArgs = {
        nombre: '${contact.profile_name}',
        interes: '${especialidad}',
        campos: { fecha_preferida: '${disponibilidad.requested_start}' },
      }
      // Lane layout: main chain in col 0, the "pick from alternatives" path in
      // col 1, and the terminal messages in col 2 — so nothing overlaps.
      //
      // EL MEDIO DE LA RAMA LO EMITE `buildBookingSegment` (D22-4): buscar,
      // ofrecer, elegir, reservar, decidir por el estado y las dos salidas de
      // rendición. Esta rama conserva lo suyo — las dos preguntas de entrada, la
      // toma de datos de la visita y los mensajes de la cita hecha — y se las
      // entrega ya emitidas para que el tramo las intercale donde van.
      const segment = buildBookingSegment({
        ids: segmentIds,
        specialtyExpr: '${especialidad}',
        whenExpr: '${fecha_preferida}',
        bookArgs: {
          patient_name: '${contact.profile_name}',
          phone: '${contact.phone}',
          // Visit intake captured just before booking — stored on the cita.
          reason: '${motivo}',
          first_visit: '${primera_vez}',
          insurer: '${cobertura}',
        },
        leadArgs: citaLeadArgs,
        copy: {
          ofrecerExacto: copy.ofrecerExacto,
          avisoAlternativas: copy.avisoAlternativas,
          listarOpciones: copy.listarOpciones,
          horarioOcupado: copy.horarioOcupado,
          noOfrece: action.notOffered,
          sinCupo: action.noSlots,
        },
        // El tramo empieza en el renglón 2 del carril: arriba van las dos
        // preguntas de entrada de esta rama.
        layout: { x: laneX, y: TOP, col: COL, row: ROW, firstRow: 2 },
        intake: {
          nodes: [
            {
              // Visit intake, asked once a slot is picked and before we book. `open`
              // so a mid-intake question ("¿cuánto cuesta?") is answered from KB and
              // the ask repeats, instead of being stored as the motivo.
              id: n.motivo,
              type: 'ask',
              position: { x: gx(0), y: gy(8) },
              config: {
                prompt: copy.motivoPrompt,
                var: 'motivo',
                extract: { kind: 'open' },
                max_retries: 2,
                on_unexpected: { mode: 'excurse', allow: ['kb'] },
                // Ask fresh: don't mine the motivo from the earlier "quiero una cita
                // de X" message (that captured the specialty, not the reason).
                no_prefill: true,
              },
            },
            {
              id: n.primera,
              type: 'ask',
              position: { x: gx(1), y: gy(8) },
              config: {
                prompt: copy.primeraVisitaPrompt,
                var: 'primera_vez',
                extract: { kind: 'boolean' },
                max_retries: 2,
              },
            },
            {
              // Insurance capture: their aseguradora, or "particular". book_appointment
              // derives coverage and remembers a real insurer on the person's profile.
              id: n.seguro,
              type: 'ask',
              position: { x: gx(2), y: gy(8) },
              config: {
                prompt: copy.coberturaPrompt,
                var: 'cobertura',
                extract: { kind: 'open' },
                max_retries: 2,
                on_unexpected: { mode: 'excurse', allow: ['kb'] },
                // Ask fresh: never infer coverage from something said earlier.
                no_prefill: true,
              },
            },
          ],
          edges: [
            { from: n.motivo, to: n.primera },
            { from: n.primera, to: n.seguro },
          ],
          entryId: n.motivo,
          exitId: n.seguro,
        },
        booked: {
          nodes: [
            {
              id: n.confirmada,
              type: 'msg',
              position: { x: gx(0), y: gy(11) },
              config: {
                text: copy.citaConfirmada,
              },
            },
            {
              id: n.pendiente,
              type: 'msg',
              position: { x: gx(1), y: gy(11) },
              config: {
                text: copy.citaPendiente,
              },
            },
          ],
          edges: [
            { from: n.confirmada, to: 'fin_ok' },
            { from: n.pendiente, to: 'fin_ok' },
          ],
          confirmedId: n.confirmada,
          pendingId: n.pendiente,
        },
      })
      nodes.push(
        {
          id: n.esp,
          type: 'ask',
          position: { x: gx(0), y: gy(0) },
          config: {
            prompt: action.specialtyPrompt,
            var: 'especialidad',
            // `open`: a mid-booking question ("¿cuánto cuesta?") is treated as a
            // digression → answered from the KB, then the ask repeats (excurse).
            extract: { kind: 'open' },
            max_retries: 3,
            on_unexpected: { mode: 'excurse', allow: ['kb'] },
          },
        },
        {
          id: n.fecha,
          type: 'ask',
          position: { x: gx(0), y: gy(1) },
          config: {
            prompt: action.datePrompt,
            // `when`: captures a date/time answer (even "¿el jueves?"), but a
            // question with no date signal is a digression → KB answer + re-ask.
            var: 'fecha_preferida',
            extract: { kind: 'when' },
            max_retries: 3,
            on_unexpected: { mode: 'excurse', allow: ['kb'] },
          },
        },
        ...segment.nodes,
      )
      edges.push(
        { from: 'discover', to: n.esp, when: whenForIntent('intent', c.key) },
        { from: n.esp, to: n.fecha },
        { from: n.fecha, to: segment.entryId },
        ...segment.edges,
      )
      laneX += 3 * COL + LANE_GAP // this lane used 3 columns
    } else if (c.action.kind === 'call') {
      // A single `call` node: the published sub-flow takes the turn and runs its
      // own questions. Its outgoing edge is mandatory — without one the runtime
      // has no resume point and escalates with `dead_end`.
      const cid = `llamar_${c.key}`
      nodes.push({
        id: cid,
        type: 'call',
        position: { x: gx(0), y: gy(0) },
        config: { skill_id: c.action.skillId },
      })
      edges.push({ from: 'discover', to: cid, when: whenForIntent('intent', c.key) })
      edges.push({ from: cid, to: 'fin_ok' })
      laneX += COL + LANE_GAP
    } else if (c.action.kind === 'quote') {
      // Real price quote from the catálogo: ask the estudio → lookup_price →
      // quote the real price + offer to book → not in catalog → coordinator.
      const action = c.action
      const k = c.key
      const q = {
        pedir: `pedir_${k}`,
        buscar: `buscar_${k}`,
        hay: `hay_${k}`,
        precio: `precio_${k}`,
        ofrecer: `ofrecer_${k}`,
        decidir: `decidir_${k}`,
        sin: `sin_${k}`,
        finSin: `fin_sin_${k}`,
      }
      nodes.push(
        {
          id: q.pedir,
          type: 'ask',
          position: { x: gx(0), y: gy(0) },
          config: {
            prompt: action.estudioPrompt,
            var: 'estudio',
            extract: { kind: 'open' },
            max_retries: 3,
            on_unexpected: { mode: 'excurse', allow: ['kb'] },
          },
        },
        {
          id: q.buscar,
          type: 'act',
          position: { x: gx(0), y: gy(1) },
          config: { tool: 'lookup_price', args: { study: '${estudio}' }, out: 'cotizacion' },
        },
        { id: q.hay, type: 'dec', position: { x: gx(0), y: gy(2) }, config: {} },
        {
          id: q.precio,
          type: 'msg',
          position: { x: gx(0), y: gy(3) },
          config: {
            text: copy.precio,
          },
        },
        {
          id: q.ofrecer,
          type: 'ask',
          position: { x: gx(0), y: gy(4) },
          config: {
            prompt: copy.ofrecerAgendar,
            var: 'agendar_cotizacion',
            extract: { kind: 'boolean' },
            max_retries: 2,
          },
        },
        { id: q.decidir, type: 'dec', position: { x: gx(0), y: gy(5) }, config: {} },
        {
          id: q.sin,
          type: 'msg',
          position: { x: gx(1), y: gy(2) },
          config: { text: action.notFound },
        },
        {
          // F6-5: "en un momento te comunico con un coordinador" tiene que
          // cumplirse en el turno que lo dice. El estudio no está en el catálogo
          // de precios, que es exactamente lo que la razón nombra.
          id: q.finSin,
          type: 'fin',
          position: { x: gx(1), y: gy(3) },
          config: { kind: 'handoff', reason: 'cotizacion_sin_precio' },
        },
      )
      // The "sí, agéndalo" jump target: reuse the booking branch if present. The
      // estudio pre-fills the booking's especialidad. Sin rama de reserva — o
      // con un id que este agente no emitió — la resolución del final de la
      // compilación lo manda con una persona.
      const bookTo: StarterEdge = {
        from: q.decidir,
        to: action.bookEntry ?? BOOK_ENTRY_FALLBACK,
        when: 'agendar_cotizacion == true',
      }
      bookEntryEdges.push(bookTo)
      edges.push(
        { from: 'discover', to: q.pedir, when: whenForIntent('intent', c.key) },
        { from: q.pedir, to: q.buscar },
        { from: q.buscar, to: q.hay },
        { from: q.hay, to: q.precio, when: 'cotizacion.found == true' },
        { from: q.hay, to: q.sin, when: 'true' },
        { from: q.precio, to: q.ofrecer },
        { from: q.ofrecer, to: q.decidir },
        bookTo,
        { from: q.decidir, to: 'cierre', when: 'true' }, // declined → say goodbye, don't go silent
        { from: q.sin, to: q.finSin },
      )
      laneX += 2 * COL + LANE_GAP // this lane used 2 columns
    } else if (c.action.kind === 'manage') {
      // Real reschedule/cancel: find the patient's cita (by their WhatsApp phone,
      // else a confirmation number) → ask what to do → cancel_booking, or cancel
      // + reagendar (jump into the booking branch). Not found → coordinator.
      const action = c.action
      const k = c.key
      const m = {
        buscar: `buscar_${k}`,
        hay: `hay_${k}`,
        folio: `pedir_folio_${k}`,
        buscarFolio: `buscar_folio_${k}`,
        hayFolio: `hay_folio_${k}`,
        accion: `accion_${k}`,
        decidir: `decidir_${k}`,
        cancelar: `cancelar_${k}`,
        cancelada: `cancelada_${k}`,
        reagendarMsg: `reagendar_msg_${k}`,
        reagendarAct: `reagendar_act_${k}`,
        reagendarFin: `fin_reagendar_${k}`,
        dejar: `dejar_${k}`,
        sin: `sin_${k}`,
        finSin: `fin_sin_${k}`,
      }
      const foundCita = copy.citaEncontrada
      nodes.push(
        {
          id: m.buscar,
          type: 'act',
          position: { x: gx(0), y: gy(0) },
          config: { tool: 'lookup_booking', args: { key: '${contact.phone}' }, out: 'cita_actual' },
        },
        { id: m.hay, type: 'dec', position: { x: gx(0), y: gy(1) }, config: {} },
        {
          id: m.folio,
          type: 'ask',
          position: { x: gx(1), y: gy(1) },
          config: {
            prompt: action.folioPrompt,
            var: 'folio_cita',
            extract: { kind: 'string' },
            max_retries: 2,
          },
        },
        {
          id: m.buscarFolio,
          type: 'act',
          position: { x: gx(1), y: gy(2) },
          config: { tool: 'lookup_booking', args: { key: '${folio_cita}' }, out: 'cita_actual' },
        },
        { id: m.hayFolio, type: 'dec', position: { x: gx(1), y: gy(3) }, config: {} },
        {
          id: m.accion,
          type: 'ask',
          position: { x: gx(0), y: gy(3) },
          config: {
            prompt: foundCita,
            var: 'accion_cita',
            extract: { kind: 'string' },
            max_retries: 2,
            // Always ask fresh: never mine "cancelar"/"reagendar" from the
            // earlier message that routed the patient into this branch.
            no_prefill: true,
          },
        },
        { id: m.decidir, type: 'dec', position: { x: gx(0), y: gy(4) }, config: {} },
        {
          id: m.cancelar,
          type: 'act',
          position: { x: gx(0), y: gy(5) },
          config: {
            tool: 'cancel_booking',
            args: { booking_id: '${cita_actual.id}' },
            out: 'cancelacion',
          },
        },
        {
          id: m.cancelada,
          type: 'msg',
          position: { x: gx(0), y: gy(6) },
          config: {
            text: copy.citaCancelada,
          },
        },
        {
          id: m.reagendarMsg,
          type: 'msg',
          position: { x: gx(1), y: gy(5) },
          config: {
            // D22-7 — SIN AGENDA, LA PROMESA CAMBIA. Con la rama de reserva
            // retirada, "buscamos un nuevo horario" sería una frase que este
            // agente ya no puede cumplir.
            text: anyProcessBooks ? copy.reagendarConPersona : copy.reagendarAviso,
          },
        },
        ...(anyProcessBooks
          ? // D22-7 — Y NO SE CANCELA NADA. Cancelar y después pasar con una
            // persona deja al paciente SIN cita y con una promesa, que es peor
            // que lo de hoy. El traspaso es un `fin.handoff` y no el nodo
            // `pasar` compartido por lo mismo que las otras salidas de esta
            // rama (F6-5): después de un `msg` que promete una persona, el
            // runtime pausa si el siguiente nodo no es `ask`/`fin`/`route`, y
            // la promesa saldría sola.
            ([
              {
                id: m.reagendarFin,
                type: 'fin',
                position: { x: gx(1), y: gy(6) },
                config: { kind: 'handoff', reason: 'cita_reagendar_sin_agenda' },
              },
            ] as StarterNode[])
          : ([
              {
                id: m.reagendarAct,
                type: 'act',
                position: { x: gx(1), y: gy(6) },
                config: {
                  tool: 'cancel_booking',
                  args: { booking_id: '${cita_actual.id}' },
                  out: 'cancelacion',
                },
              },
            ] as StarterNode[])),
        {
          id: m.dejar,
          type: 'msg',
          position: { x: gx(2), y: gy(5) },
          config: { text: copy.citaSinCambios },
        },
        {
          id: m.sin,
          type: 'msg',
          position: { x: gx(2), y: gy(3) },
          config: { text: action.notFound },
        },
        {
          // F6-5: "te paso con una persona del equipo para revisarlo" se cumple
          // aquí mismo. Ni el teléfono ni el folio dieron con una cita, que es
          // lo que la razón dice.
          id: m.finSin,
          type: 'fin',
          position: { x: gx(2), y: gy(4) },
          config: { kind: 'handoff', reason: 'cita_no_encontrada' },
        },
      )
      // El "reagendar" salta a la entrada de la rama de reserva; sin ella, con
      // una persona (la misma resolución que la cotización). Con un proceso que
      // agenda no hay salto que resolver: esa pata ya termina con una persona
      // por su cuenta, y sin haber cancelado nada.
      const reagendarTo: StarterEdge | null = anyProcessBooks
        ? null
        : { from: m.reagendarAct, to: action.bookEntry ?? BOOK_ENTRY_FALLBACK }
      if (reagendarTo !== null) bookEntryEdges.push(reagendarTo)
      edges.push(
        { from: 'discover', to: m.buscar, when: whenForIntent('intent', c.key) },
        { from: m.buscar, to: m.hay },
        // Found by phone → offer options; else ask for the confirmation number.
        { from: m.hay, to: m.accion, when: 'cita_actual.id.length > 0' },
        { from: m.hay, to: m.folio, when: 'true' },
        { from: m.folio, to: m.buscarFolio },
        { from: m.buscarFolio, to: m.hayFolio },
        { from: m.hayFolio, to: m.accion, when: 'cita_actual.id.length > 0' },
        { from: m.hayFolio, to: m.sin, when: 'true' },
        // Interpret the reply (deterministic substring match).
        { from: m.accion, to: m.decidir },
        { from: m.decidir, to: m.cancelar, when: 'accion_cita ~= "cancel"' },
        {
          from: m.decidir,
          to: m.reagendarMsg,
          // filtrex uses the `or` keyword, not `||` (which is a lexical error).
          when: 'accion_cita ~= "reagend" or accion_cita ~= "cambi" or accion_cita ~= "mover" or accion_cita ~= "otro"',
        },
        { from: m.decidir, to: m.dejar, when: 'true' },
        { from: m.cancelar, to: m.cancelada },
        { from: m.cancelada, to: 'fin_ok' },
        ...(reagendarTo === null
          ? [{ from: m.reagendarMsg, to: m.reagendarFin }]
          : [{ from: m.reagendarMsg, to: m.reagendarAct }, reagendarTo]),
        { from: m.dejar, to: 'fin_ok' },
        { from: m.sin, to: m.finSin },
      )
      laneX += 3 * COL + LANE_GAP // this lane used 3 columns
    } else {
      // 'end' says goodbye (cierre) before the terminal fin.ok — never a silent stop.
      const to =
        c.action.kind === 'answer' ? 'responder' : c.action.kind === 'handoff' ? 'pasar' : 'cierre'
      edges.push({ from: 'discover', to, when: whenForIntent('intent', c.key) })
    }
  })
  edges.push({ from: 'discover', to: 'responder', when: 'true' }) // default: answer from the KB
  edges.push({ from: 'responder', to: 'discover' }) // loop
  edges.push({ from: 'pasar', to: 'discover' }) // resume after handoff

  // El salto a la reserva se resuelve contra lo que REALMENTE se emitió: un
  // `bookEntry` que nombra una rama que este agente no tiene deja de ser un
  // destino y pasa a ser una persona.
  const emittedIds = new Set(nodes.map((node) => node.id))
  bookEntryEdges.forEach((edge) => {
    if (!emittedIds.has(edge.to)) edge.to = BOOK_ENTRY_FALLBACK
  })
  const orchestrator: StarterFlow = { nodes, edges }
  // Y NINGUNA ARISTA PUEDE QUEDAR COLGANDO. Lo que el compilador emite se
  // revisa aquí, entero — el orquestador y cada flujo de captura —, porque una
  // arista hacia un nodo que no existe no truena al compilar sino en la
  // conversación, turnos después y delante del cliente (`invalid_state`).
  assertNoDanglingEdges(orchestrator)
  captureFlows.forEach((compiled) => assertNoDanglingEdges(compiled.flow))

  return {
    orchestrator,
    captureFlows,
    // D6: el consentimiento es una PRIMERA etapa VIRTUAL. Se devuelve para que
    // la vista previa la dibuje; NUNCA se compila a nodos, porque quien la
    // ejecuta es la puerta del pipeline (paso 3b) por identidad de canal.
    // TODO(Wave 3 — la vista previa): dibujarla al frente del proceso.
    virtualStages: input.consentConfigured
      ? [{ kind: 'consent' as const, name: CONSENT_VIRTUAL_STAGE_NAME }]
      : [],
    pendingCaptureLinks,
    agendaRefusals,
  }
}
